/* eslint-disable no-unused-vars */
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { Alert, Checkbox, Divider, FormControlLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import SendSharpIcon from '@mui/icons-material/SendSharp';
import ModeSharpIcon from '@mui/icons-material/ModeSharp';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { AppDispatch } from '../../Redux/store';
import { getActiveCourse, getNextCourse, getPendingCourse } from '../../Redux/Actions/PunctualCourseActions';
import { SHOW_ERROR, SHOW_SUCCESS } from '../../Redux/Reducers/MessageReducers';
import DialogLayout from '../../Components/DialogLayout';
// Fenêtre repensée (maquettes 3a, 3b) : mêmes props, l'ancien DetailCours reste disponible
import DetailCours, { CoursProps, DetailCoursV2Header } from '../../Components/App/Courses/DetailCoursV2';
import { isRelationTeachrCourse } from '../DeclarationV2/components/relationTeachr';
// Modification d'un cours à venir en page (même gabarit que les brouillons v2), la modale reste en place
import { courseEditPath } from '../CourseEditV2/paths';
import CourseTravelProofUpload from '../../Components/App/Courses/CourseTravelProofUpload';
import FormEditCourse from '../../Components/App/Courses/FormEditCourse';
import SessionDescriptionCard from '../DeclarationCourse/Components/SessionDescriptionCard';
import { normalizeSessionDescriptionOverride, sessionDescriptionOverrideError } from '../../Utils/sessionDescriptionOverride';
import PunctualCourseRepository from '../../Repositories/PunctualCourseRepository';
import CourseProposalRepository from '../../Repositories/CourseProposalRepository';
import ImageRepository from '../../Repositories/ImageRepository';
import { PunctualCourseDashboardUpdate } from '../../Objects/models/PunctualCourseDashboardUpdate';
import CourseProposalDashboard from '../../Objects/models/CourseProposalDashbordUpdate';
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtClientShare, vtCoursePrixTotal, vtCourseRemuneration, vtTtcFromHourlyPaid } from '../../Utils/VerifiedTrainerAmounts';
import { describeMonthlyQuota, monthlyQuotaOf } from '../../Utils/monthlyQuota';
import {
    capitalizeFirstLetter,
    displayError,
    displayStatutCourseDetail,
    displayUploadProof,
    displayUploadProofs,
    getMinute,
    // handleDeclareCourse,
    handleSubmitCourse,
    validateDateTime,
    validateDeposit,
    validatePrice,
    validateTerms,
} from '../../Utils/Function';
import Functions from '../../Helpers/Functions';
import { useTailwindBrand } from '../../Components/shared';
import {
    ENV_VERIFIED_TRAINER,
    getActiveClientBrandConfig,
    getActivePrestationBrandConfig,
    isBrandActive,
    isDepositEnabled,
} from '../../config/environment.config';
import { CourseType, CourseTypeConfig } from '../../config/COURSE_TYPE_CONFIG';
import AlertInfo from '../../Components/AlertInfo';
import { useTravelScenario } from '../DeclarationCourse/Travel/TravelModeSelector';
import TravelStore from '../DeclarationCourse/Travel/TravelStore';
import { describeExternalOverlap, fetchExternalOverlap, punctualSessionRow } from '../DeclarationCourse/sessionConflicts';
import { describeOverlapApiError } from '../DeclarationCourse/overlapMessages';
import SessionList, {
    MIN_SESSIONS_MONTH,
    SessionRow,
    apiSessionsToRows,
    sessionEnd,
    sessionStart,
    sessionsToApiPayload,
    sessionsTotalHours,
    validateSessionRows,
} from '../DeclarationCourse/SessionList';
import BlockingPopup from '../../Components/BlockingPopup';
// import { CourseType } from './ListCourse';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CourseRowActionsProps {
    cours: any;
    teachr: any;
    type: CourseType;
    dataDispatch: any;
    forChild_id: number;
    client_id: number;
    startAt?: any;
    handleSubmitDelete: (coursId: number, clientId: number, setLoad: (value: React.SetStateAction<boolean>) => void, setOption: React.Dispatch<React.SetStateAction<OptionsProps>>) => void
    config: CourseTypeConfig
    child?: any;
}
export interface OptionsProps {
    edit: boolean;
    editAndDeclare: boolean;
    deletes: boolean;
    declare: boolean;
    showDetail: boolean;
    isOver: boolean;
    openIsOver: boolean;
}

type formType = {
    duration: number;
    date: Date | string;
    datesValues: Date | string;
    teachrPrice: string;
    startTime: Date | string;
    endTime: Date | string;
    deposit: number;
    proofDeposit?: string | any;
    proofDeposits?: any[];
    depositDate?: Date | string;
    terms?: boolean;
    /** Déroulement propre à ce cours (null : description habituelle du prof). */
    sessionDescriptionOverride?: string | null;
};

const MAX_PROOFS = 8;

// ─── Helper ───────────────────────────────────────────────────────────────────

const calcDateTimeValue = (dates: any, value: string | Date, isPunctualMonth: boolean, adjustMonth?: boolean): Date => {
    if (isPunctualMonth) {
        const base = adjustMonth ? moment(value).subtract(1, 'M') : moment(value);
        return base.toDate();
    }
    return dates.set('hour', moment(value).hours()).set('minutes', moment(value).minutes()).toDate();
};

// ─── Composant ────────────────────────────────────────────────────────────────

const CourseRowActions: React.FC<CourseRowActionsProps> = ({
    cours,
    teachr,
    type,
    dataDispatch,
    forChild_id,
    client_id,
    startAt: startAtProp,
    handleSubmitDelete,
    config,
    child,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { classes, brandConfig } = useTailwindBrand();
    const isCoursePass = type === 'brouillon';
    const fullName = [cours?.firstName, cours?.lastName].filter(Boolean).join(' ');
    const now = moment().format('YYYY-MM-DD');

    const startAt = useMemo(() => {
        if (startAtProp) return startAtProp?.date;
        return type === 'avenir' || type === 'brouillon'
            ? moment().add(1, 'month').startOf('month').toDate()
            : moment().startOf('month').toDate();
    }, [startAtProp, type]);

    // ── Dates de base ──────────────────────────────────────────────────────────
    const startDate = isCoursePass ? moment(cours.startDatetime).add(1, 'M').toDate() : moment(cours.startDatetime).toDate();
    const endDate = isCoursePass ? moment(cours.endDatetime).add(1, 'M').toDate() : moment(cours.endDatetime).toDate();
    const depositDate = cours?.depositDate ? (isCoursePass ? moment().toDate() : moment(cours?.depositDate).toDate()) : moment().toDate();

    const modifiedTime = moment(moment(startDate).format('HH:mm'), 'HH:mm');
    const modifiedEndTime = moment(moment(endDate).format('HH:mm'), 'HH:mm');
    const conditionScoop = isBrandActive(ENV_VERIFIED_TRAINER) || (teachr?.scoop) ? true : false
    // Transmis à l'URSSAF : une demande de paiement existe (statut renvoyé par l'API)
    const isTransmittedToUrssaf = cours?.urssafServiceStatus != null || cours?.urssafServiceStatus_id != null;
    const errorsStatusDescription = cours?.errorsStatusDescription?.[0]?.description ?? null;
    const hourPrice = (parseFloat(cours?.teachrPrice || '0') / (1 - (teachr?.defaultFees || 0)) / 2).toFixed(2);
    const isTeachrContact = cours?.customFees?.is_teachr_contact;
    const defaultResult = [moment(startDate).format('YYYY-MM-DD'), moment(endDate).format('YYYY-MM-DD')];
    const defaultFees = parseFloat(teachr?.defaultFees ?? '0') || 0;
    const netMultiplier = 1 - defaultFees;
    // Verified Trainer : plafond et montants déclarés en prix total TTC (cours.amount est déjà la somme TTC des charges du mois)
    const isVt = isVerifiedTrainer();
    const maxNet = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;
    const coursesThisMonth = cours?.coursesThisRollingMonth ?? 0;
    // Compteur partagé : reste sur 8 tous profs confondus, et part du prof connecté
    const quotaText = describeMonthlyQuota(monthlyQuotaOf(coursesThisMonth, cours?.coursesMonthByThisTeachr));
    const alreadyDeclaredAmount = isVt ? parseFloat(cours?.amount ?? '0') : parseFloat(cours?.amount ?? '0') * netMultiplier;
    const currentMonthDisplayName = capitalizeFirstLetter(moment().locale('fr').format('MMMM'));
    // const childHoursStr = child?.duration === 0 ? '0h' : (Functions.renderDuration(child?.duration) || '0h');

    // ── State ──────────────────────────────────────────────────────────────────
    const [load, setLoad] = useState(false);
    const [option, setOption] = useState<OptionsProps>({
        edit: false,
        editAndDeclare: false,
        deletes: false,
        declare: false,
        showDetail: false,
        isOver: cours?.chapter === 'over' && !isCoursePass,
        openIsOver: false,
    });
    const [selectedCourse, setSelectedCourse] = useState<any>(cours);
    const [form, setForm] = useState<formType>({
        duration: cours?.duration / 2,
        date: startDate,
        datesValues: '',
        teachrPrice: hourPrice,
        startTime: modifiedTime.toDate(),
        endTime: modifiedEndTime.toDate(),
        // Verified Trainer : aucun acompte, quel que soit l'historique du cours
        deposit: isDepositEnabled() ? cours?.deposit : null,
        proofDeposit: '',
        proofDeposits: [],
        depositDate,
        terms: false,
        sessionDescriptionOverride: cours?.sessionDescriptionOverride ?? null,
    });
    // description habituelle du prof (remontée par la carte) et erreur de saisie de la surcharge
    const [genericDescription, setGenericDescription] = useState<string | null>(null);
    const [descriptionError, setDescriptionError] = useState('');
    const [error, setError] = useState({ price: false, date: false, dates: false, endTime: false, proofDeposit: false, dateDeposit: false, terms: false, deposit: false });
    const [errorMsg, setErrorMsg] = useState({ price: '', date: '', dates: '', endTime: '', proofDeposit: '', dateDeposit: '', deposit: '' });
    const [errorMonth, setErrorMonth] = useState({ error: false, msg: '' });
    // P1 – séances détaillées d'un cours mensuel (brouillon : décalées de +1 mois,
    // comme les dates du flux existant). Les séances passées restent modifiables
    // tant que le cours n'est ni terminé ni transmis à l'URSSAF, et dans ce cas
    // la modification n'est pas proposée du tout.
    const [sessionRows, setSessionRows] = useState<SessionRow[]>(() =>
        cours?.isPunctualMonth ? apiSessionsToRows(cours?.sessions, isCoursePass ? 1 : 0) : []
    );
    const [blockingSessions, setBlockingSessions] = useState<{ open: boolean; message: string; title?: string; actionLabel?: string }>({ open: false, message: '' });
    // Deux séances minimum, sauf pour un ancien cours du mois enregistré avec
    // une seule séance (reprise P1) : il reste modifiable tel quel.
    const minSessions = (Array.isArray(cours?.sessions) ? cours.sessions.length : 0) >= MIN_SESSIONS_MONTH
        ? MIN_SESSIONS_MONTH
        : 1;

    // Synchronise séances + champs dérivés du formulaire (durée totale, période) :
    // les validations existantes (quota, plafond, acompte) restent inchangées.
    const handleSessionsChange = (rows: SessionRow[]) => {
        setSessionRows(rows);
        const starts = rows.map(sessionStart).filter(Boolean) as moment.Moment[];
        const ends = rows.map(sessionEnd).filter(Boolean) as moment.Moment[];
        const total = sessionsTotalHours(rows);
        setForm((p) => ({
            ...p,
            duration: total,
            date: starts.length ? moment.min(starts).toDate() : p.date,
            startTime: starts.length ? moment.min(starts).toDate() : p.startTime,
            endTime: ends.length ? moment.max(ends).toDate() : p.endTime,
        }));
    };

    // ── Sync form on edit open ─────────────────────────────────────────────────
    useEffect(() => {
        if ((option.edit || option.editAndDeclare) && selectedCourse) {
            const newStart = isCoursePass ? moment(selectedCourse.startDatetime).add(1, 'M').toDate() : moment(selectedCourse.startDatetime).toDate();
            const newEnd = isCoursePass ? moment(selectedCourse.endDatetime).add(1, 'M').toDate() : moment(selectedCourse.endDatetime).toDate();
            const rawDeposit = selectedCourse.depositDate ? moment(selectedCourse.depositDate) : moment();
            const newDeposit = isCoursePass ? rawDeposit.clone().add(1, 'M').toDate() : rawDeposit.toDate();
            const newModStart = cours?.isPunctualMonth ? moment(newStart) : moment(moment(newStart).format('HH:mm'), 'HH:mm');
            const newModEnd = cours?.isPunctualMonth ? moment(newEnd) : moment(moment(newEnd).format('HH:mm'), 'HH:mm');
            const newPrice = (parseFloat(selectedCourse?.teachrPrice || '0') / (1 - (teachr?.defaultFees || 0)) / 2).toFixed(2);
            setForm({
                duration: selectedCourse?.duration / 2,
                date: newStart,
                datesValues: form.datesValues,
                teachrPrice: newPrice,
                startTime: newModStart.toDate(),
                endTime: newModEnd.toDate(),
                deposit: isDepositEnabled() ? selectedCourse?.deposit : null,
                proofDeposit: '',
                proofDeposits: [],
                depositDate: newDeposit,
                terms: false,
                sessionDescriptionOverride: selectedCourse?.sessionDescriptionOverride ?? null,
            });
            setDescriptionError('');
            // P1 – re-synchronise les séances éditables à l'ouverture du dialogue.
            // Brouillon : les séances gardent leur jour de semaine le mois
            // suivant (apiSessionsToRows), la période du formulaire suit les
            // séances et non startDatetime + 1 mois.
            if (cours?.isPunctualMonth) {
                handleSessionsChange(apiSessionsToRows(selectedCourse?.sessions, isCoursePass ? 1 : 0));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [option.edit, option.editAndDeclare, selectedCourse, isCoursePass, teachr?.defaultFees, cours?.isPunctualMonth]);

    // ── Calculs ────────────────────────────────────────────────────────────────
    const amount = form.duration * (parseFloat(form.teachrPrice) || 0);
    const depositStay = (): string => {
        const diff = amount - form.deposit;
        return (diff > 0 ? diff : 0).toFixed(2);
    };
    const formDateForQuota = form.date && dayjs.isDayjs(form.date)
        ? moment((form.date as any).format('YYYY-MM-DD'), 'YYYY-MM-DD')
        : moment(form.date);
    const isCurrentMonthDecl = formDateForQuota.format('YYYY-MM') === moment().format('YYYY-MM');
    const newDeclAmount = isVt
        ? vtTtcFromHourlyPaid(form.teachrPrice, form.duration)
        : form.duration * (parseFloat(form.teachrPrice) || 0) * 2 * netMultiplier;
    const totalDeclIfAdded = alreadyDeclaredAmount + (isNaN(newDeclAmount) ? 0 : newDeclAmount);
    // Plafond atteignable : seul un dépassement bloque, comparé au centime (arrondis flottants)
    const wouldExceedLimit = isCurrentMonthDecl && !!form.teachrPrice && !isNaN(newDeclAmount) && newDeclAmount > 0
        && Math.round(totalDeclIfAdded * 100) / 100 > Math.round(maxNet * 100) / 100;

    const formDate = form.date && dayjs.isDayjs(form.date)
        ? moment((form.date as any).format('YYYY-MM-DD'), 'YYYY-MM-DD')
        : moment(form.date);
    const dateCourse = isCoursePass ? formDate.subtract(1, 'M') : formDate;
    const startDateTime = calcDateTimeValue(dateCourse, form.startTime, cours?.isPunctualMonth, isCoursePass);
    const endDateTime = calcDateTimeValue(dateCourse, form.endTime, cours?.isPunctualMonth, isCoursePass);
    const dateOfDeposit = form.depositDate
        ? (dayjs.isDayjs(form.depositDate)
            ? moment((form.depositDate as any).format('YYYY-MM-DD')).format('YYYY-MM-DD')
            : moment(form.depositDate as Date).format('YYYY-MM-DD'))
        : null;

    const coursePayload = {
        forChild_id,
        chapter: option.isOver ? 'over' : '',
        address_id: cours?.address_id,
        subjectAsked_id: cours?.subjectAsked_id,
        startDateTime: isCoursePass ? moment(startDateTime).add(1, 'M').toDate() : startDateTime,
        endDateTime: isCoursePass ? moment(endDateTime).add(1, 'M').toDate() : endDateTime,
        isRemote: false,
        teachr_id: teachr?.id,
        teachrId: teachr?.['@id'],
        isCourseInTheHour: false,
        duration: (form.duration * 2).toString(),
        isDirectCourse: true,
        isFiscalReductionEnabled: false,
        paymentMean: 'DRAFT',
        aiciActivated: true,
        chapterExtraInformation: '',
        globalExtraInformation: '',
        isPunctualMonth: cours?.isPunctualMonth,
        isPassedCourse: false,
        // P1 – séances détaillées du cours mensuel (déjà décalées de +1 mois
        // pour un brouillon, comme startDateTime/endDateTime)
        sessions: cours?.isPunctualMonth ? sessionsToApiPayload(sessionRows) : null,
        description: null,
        teachrPrice: (parseFloat(form.teachrPrice) * 2 * (1 - teachr?.defaultFees)).toString(),
        proofDeposit: !cours?.isPunctualMonth && form.proofDeposit ? form.proofDeposit.id : null,
        proofDeposits: cours?.isPunctualMonth && form.proofDeposits && form.proofDeposits.length > 0
            ? form.proofDeposits.map((p: any) => p?.id).filter(Boolean)
            : null,
        deposit: form.deposit ? form.deposit : null,
        depositDate: dateOfDeposit,
        // brouillon re-déclaré : nouvelle attestation d'acompte pour le nouveau cours
        depositAttestationAccepted: !!form.deposit && form.terms === true,
        // déroulement propre à ce cours, null = description habituelle
        sessionDescriptionOverride: normalizeSessionDescriptionOverride(form.sessionDescriptionOverride, genericDescription),
    };

    const handleCloseDeclare = () => setOption((p) => ({ ...p, declare: false }));
    const handleCloseEdit = () => setOption((p) => ({ ...p, edit: false }));

    const dataPunctual = {
        course: coursePayload,
        data: {
            value: 0,
            isCoursePass: true,
            setLoad,
            teachr,
            clientId: client_id,
            isCours: coursePayload?.isPunctualMonth,
            dispatch,
            punctualId: cours?.id,
            handleCloseDeclare,
            handleCloseEdit,
            dataDispatch,
            setClose: handleCloseDeclare,
            type: type,
            config,
            oldCourseId: cours.id,
            travelProofFile: null as File | null,
            travelProofFromSubscription: false as boolean,
        },
    };

    // ── Scénarios distance (P5) : attestation 60–160 km, preuve > 160 km ──────
    const { checkTravelScenario, travelModals } = useTravelScenario(teachr);
    const childForTravel = {
        clientId: client_id,
        // adresse du cours : le scénario distance est décidé sur elle
        addressId: cours?.address_id ?? child?.addressId ?? null,
        childFirstName: child?.childFirstName ?? cours?.forChildFirstName ?? '',
        childLastName: child?.childLastName ?? cours?.forChildLastName ?? '',
    };
    const submitWithTravelCheck = (onSubmit: () => void) => {
        // P6 : les dates finales du cours permettent de vérifier que
        // l'abonnement de transports en commun couvre bien toute la période
        checkTravelScenario(childForTravel, () => {
            dataPunctual.data.travelProofFile = TravelStore.getProofFile(client_id);
            // P6 : l'abonnement de transports en commun valide sert de preuve
            dataPunctual.data.travelProofFromSubscription = TravelStore.getUseSubscription(client_id);
            onSubmit();
        }, { start: coursePayload.startDateTime, end: coursePayload.endDateTime });
    };

    // ── editAndDeclare effect ──────────────────────────────────────────────────
    useEffect(() => {
        if (option.editAndDeclare) {
            setOption((p) => ({ ...p, editAndDeclare: false }));
            dispatch(getActiveCourse({ currentPage: 1, isData: false, global: { id: teachr?.id, nameSearch: '', sort: { var: 0, sort: 1 } } }));
            dispatch(getPendingCourse({ currentPagePending: 1, isData: false, global: { id: teachr?.id, nameSearch: '', sort: { var: 0, sort: 1 } } }));
            dispatch(getNextCourse(dataDispatch));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [option.editAndDeclare]);

    // ── Image upload ───────────────────────────────────────────────────────────
    const handleCreateImage = (file: File) => {
        ImageRepository.createImage(new File([file], 'proof-deposit', { type: file.type || 'application/pdf' }))
            .then((res) => {
                if (cours?.isPunctualMonth) {
                    // Plusieurs preuves : on accumule sans toucher à editAndDeclare
                    // (qui re-déclenche le reset du formulaire et viderait le tableau).
                    setForm((p) => ({ ...p, proofDeposits: [...(p.proofDeposits || []), res] }));
                    setError((p) => ({ ...p, proofDeposit: false }));
                } else {
                    setForm((p) => ({ ...p, proofDeposit: res }));
                    setError((p) => ({ ...p, proofDeposit: false }));
                    setOption((p) => ({ ...p, editAndDeclare: true }));
                }
            })
            .catch((error) => {
                setLoad(false)
            })
            .finally(() => { setLoad(false); if (!cours?.isPunctualMonth) setOption((p) => ({ ...p, editAndDeclare: false })); });
    };
    const handleDeletImage = () => {
        setLoad(true);
        // c'est la preuve du formulaire qui est retirée, pas celle déjà enregistrée sur le cours
        ImageRepository.deletImage(form?.proofDeposit?.id)
            .then(() => { setForm((p) => ({ ...p, proofDeposit: {} })); setOption((p) => ({ ...p, editAndDeclare: true })); })
            .catch(() => setLoad(false))
            .finally(() => { setLoad(false); setOption((p) => ({ ...p, editAndDeclare: false })); });
    };
    const handleDeletImageMulti = (index: number) => {
        const target = form.proofDeposits?.[index];
        if (!target) return;
        setLoad(true);
        ImageRepository.deletImage(target?.id)
            .then(() => { setForm((p) => ({ ...p, proofDeposits: (p.proofDeposits || []).filter((_, i) => i !== index) })); })
            .catch(() => setLoad(false))
            .finally(() => { setLoad(false); });
    };
    const setFile = (value: File) => {
        setLoad(true);
        const fileType = value.type.split('/');
        if (value.type.startsWith('image')) {
            Functions.resizeFile(value, true, fileType[1]).then((res: any) => {
                if (URL.createObjectURL(res)) handleCreateImage(res);
            });
        } else {
            handleCreateImage(value);
        }
    };

    const handleOpenDeclare = (message?: string) => {
        handleCloseDeclare()
        handleCloseMenu();
        setSelectedCourse({ ...cours, message });
        setOption((p) => ({ ...p, edit: true, declare: false }));
    }

    // Attestation sur l'honneur d'acompte à (re)certifier : brouillon, montant
    // ou date modifiés, ou cours sans attestation (ancien régime « preuve seule »).
    const needsDepositAttestation = !!form.deposit && (
        isCoursePass
        || Number(form.deposit) !== Number(cours?.deposit ?? 0)
        || dateOfDeposit !== (cours?.depositDate ? moment(cours.depositDate).format('YYYY-MM-DD') : null)
        || !cours?.depositAttestation
    );

    // Preuve d'acompte obligatoire (demande du 23 octobre 2026) : un justificatif
    // déposé dans le formulaire, ou déjà enregistré sur le cours.
    const hasDepositProof = (): boolean => {
        const newProof = cours?.isPunctualMonth
            ? (form.proofDeposits || []).length > 0
            : Object.values(form.proofDeposit || {}).length > 0;
        const existingProof = !!cours?.proofDeposit
            || (Array.isArray(cours?.proofDeposits) && cours.proofDeposits.length > 0);
        return newProof || existingProof;
    };

    // ── Validation ─────────────────────────────────────────────────────────────
    const validatePunctual = (valid?: boolean): boolean => {
        let isPriceValid = true, isDateValid = true, isEndTime = true, isProof = true;
        let isDepositDate = true, isDate = true, isErrorMonth = true, isTerms = true, deposit = true;

        const dc = form.date && dayjs.isDayjs(form.date)
            ? moment((form.date as any).format('YYYY-MM-DD'), 'YYYY-MM-DD')
            : moment(form.date);
        const monthCondition = moment(dc).format('YYYY-MM-DD') < moment(startAt).format('YYYY-MM-DD')
            ? moment(dc).format('YYYY-MM-DD') < moment(startAt).format('YYYY-MM-DD')
            : moment(dc).format('YYYY-MM') < moment().format('YYYY-MM');

        const priceV = validatePrice(form.teachrPrice, form.duration);
        // P1 – la validation d'un mensuel porte sur ses séances détaillées
        const sessionsV = cours?.isPunctualMonth ? validateSessionRows(sessionRows, { minSessions }) : null;
        const dateV = cours?.isPunctualMonth
            ? { error: !sessionsV!.isValid, message: sessionsV!.message }
            : (false as any);
        const endTV = validateDateTime(form.duration, form.startTime, form.endTime);

        setError((p) => ({ ...p, price: !priceV.isValid }));
        isPriceValid = priceV.isValid;

        if (cours?.isPunctualMonth) {
            setError((p) => ({ ...p, dates: dateV.error }));
            isDateValid = !dateV.error;
            // popup bloquant en mode édition (le flux « Déclarer » d'un brouillon
            // repasse par handleOpenDeclare avec le message plus bas)
            if (dateV.error && !(isCoursePass && valid)) {
                setBlockingSessions({ open: true, message: dateV.message });
            }
        } else {
            setError((p) => ({ ...p, endTime: !endTV.isValid }));
            isEndTime = endTV.isValid;
        }
        if (!form.date) { isDate = false; setError((p) => ({ ...p, date: true })); }
        else { setError((p) => ({ ...p, date: false })); }

        if (isCoursePass && valid) {
            if (cours?.isPunctualMonth && dateV.error) {
                handleCloseDeclare();
                handleOpenDeclare(`${dateV.message}, veuillez modifier la date de ${getActivePrestationBrandConfig()}`);
                return false;
            }
            if (!cours?.isPunctualMonth && !endTV.isValid) {
                handleCloseDeclare();
                handleOpenDeclare(`${endTV.message}, veuillez modifier la date de ${getActivePrestationBrandConfig()}`);
                return false;
            }
            if (form.deposit) {
                if (!form.terms) {
                    handleCloseDeclare();
                    handleOpenDeclare(`Vous avez opté pour la déclaration avec acompte, veuillez certifier l'attestation sur l'honneur d'acompte.`);
                    return false;
                }
                if (!dateOfDeposit) {
                    handleCloseDeclare();
                    handleOpenDeclare(`Veuillez renseigner la date d'acompte.`);
                    return false;
                }
                if (!hasDepositProof()) {
                    handleCloseDeclare();
                    handleOpenDeclare(`Veuillez joindre une preuve d'acompte (PDF, PNG ou JPEG).`);
                    return false;
                }
            }
        } else if (form.deposit) {
            // preuve de paiement obligatoire dès qu'une nouvelle attestation est
            // signée (acompte ajouté ou modifié), en plus de l'attestation
            if (needsDepositAttestation && !hasDepositProof()) {
                isProof = false;
                setError((p) => ({ ...p, proofDeposit: true }));
                setErrorMsg((p) => ({ ...p, proofDeposit: "Vous devez joindre une preuve d'acompte pour enregistrer." }));
            } else {
                setError((p) => ({ ...p, proofDeposit: false }));
            }
            if (form.deposit !== cours?.deposit || (isCoursePass && form.deposit)) {
                if (!dateOfDeposit) {
                    isDepositDate = false;
                    setError((p) => ({ ...p, dateDeposit: true }));
                } else {
                    setError((p) => ({ ...p, dateDeposit: false }));
                }
                if (!validateDeposit(form.deposit, amount, true).isValid) {
                    deposit = false;
                    setError((p) => ({ ...p, deposit: true }));
                } else {
                    setError((p) => ({ ...p, deposit: false }));
                }

            }
            if (needsDepositAttestation && !validateTerms(form.terms!).isValid) { setError((p) => ({ ...p, terms: true })); isTerms = false }
            else { setError((p) => ({ ...p, terms: false })); }
        }

        if (monthCondition) { isErrorMonth = false; setErrorMonth((p) => ({ ...p, error: true })); }
        else { setErrorMonth({ error: false, msg: '' }); }

        setErrorMsg((p) => ({
            ...p,
            price: priceV.message,
            dates: cours?.isPunctualMonth ? dateV.message : `Veuillez modifier la date de ${getActivePrestationBrandConfig()}`,
            endTime: endTV.message,
            date: isDate ? '' : `Veuillez renseigner la date de ${getActivePrestationBrandConfig()}`,
            dateDeposit: isDepositDate ? '' : "Veuillez renseigner la date d'acompte",
            deposit: validateDeposit(form.deposit, amount, form.deposit ? true : false).message,
        }));

        // Plafond mensuel montant
        let isAmountValid = true;
        if (wouldExceedLimit) {
            isAmountValid = false;
            setError((p) => ({ ...p, price: true }));
            setErrorMsg((p) => ({ ...p, price: `Total ce mois (${totalDeclIfAdded.toFixed(0)}€) dépasserait le plafond de ${maxNet.toFixed(0)}€. Changez la date.` }));
            if (isCoursePass && valid) {
                handleCloseDeclare();
                handleOpenDeclare(`Total déclaré ce mois (${totalDeclIfAdded.toFixed(0)}€) dépasserait le plafond de ${maxNet.toFixed(0)}€. Veuillez modifier la date.`);
                return false;
            }
        }

        // Surcharge de description : vide = description habituelle, sinon 30 à 1000 caractères
        const descError = sessionDescriptionOverrideError(form.sessionDescriptionOverride);
        setDescriptionError(descError);
        const isDescriptionValid = descError === '';

        return cours?.isPunctualMonth
            ? isPriceValid && isDateValid && isProof && isDate && isDepositDate && isErrorMonth && isTerms && deposit && isAmountValid && isDescriptionValid
            : isPriceValid && isEndTime && isProof && isDate && isDepositDate && isErrorMonth && isTerms && deposit && isAmountValid && isDescriptionValid;
    };

    // ── Soumissions ────────────────────────────────────────────────────────────

    const handleOpenIsOver = () => { handleCloseEdit(); setOption((p) => ({ ...p, openIsOver: true })); };
    const handleCloseIsOver = () => { handleCloseEdit(); setOption((p) => ({ ...p, openIsOver: false })); };
    const handleSubmitDeclare = () => {
        if (validatePunctual(true)) {
            submitWithTravelCheck(() => handleSubmitCourse(dataPunctual));
        }
    };
    const handleSubmitEditDeclare = async () => {
        submitWithTravelCheck(async () => {
            try { await handleSubmitCourse(dataPunctual); }
            catch (e) { console.error(e); }
            finally { setLoad(false); handleCloseIsOver(); }
        });
    };
    const handleSubmitEdit = async () => {
        setLoad(true);
        try {
            const punctualCourse = new PunctualCourseDashboardUpdate(coursePayload.startDateTime, coursePayload.endDateTime, coursePayload.duration);
            punctualCourse.chapter = option.isOver ? 'over' : '';
            // P1 – le backend remplace les séances et re-dérive start/end/duration
            punctualCourse.sessions = coursePayload.sessions;
            // déroulement propre à ce cours (null = retour à la description habituelle)
            punctualCourse.sessionDescriptionOverride = coursePayload.sessionDescriptionOverride;
            const proposals = new CourseProposalDashboard(coursePayload.teachrPrice, coursePayload.startDateTime, coursePayload.endDateTime);
            if (coursePayload.deposit) {
                proposals.deposit = coursePayload.deposit;
                if (coursePayload.depositDate) proposals.depositDate = coursePayload.depositDate;
                // attestation sur l'honneur : exigée par le serveur si le montant ou la
                // date change, ou si le cours n'en a pas encore
                proposals.depositAttestationAccepted = form.terms === true;
                // un justificatif déposé est envoyé dès que l'attestation est
                // (re)signée, même à acompte inchangé : le serveur l'exige
                const sendProof = coursePayload.deposit !== cours?.deposit || needsDepositAttestation;
                if (cours?.isPunctualMonth) {
                    if (coursePayload.proofDeposits && coursePayload.proofDeposits.length > 0 && sendProof) {
                        // acompte inchangé : les justificatifs déjà enregistrés sont conservés
                        const existingIds = coursePayload.deposit === cours?.deposit && Array.isArray(cours?.proofDeposits)
                            ? cours.proofDeposits.map((p: any) => p?.id).filter(Boolean)
                            : [];
                        proposals.proofDeposits = [...existingIds, ...coursePayload.proofDeposits];
                    }
                } else if (Object.values(form.proofDeposit).length > 0 && parseInt(cours?.proofDeposit) !== coursePayload.proofDeposit && sendProof) {
                    proposals.proofDeposit = coursePayload.proofDeposit;
                }
            } else if (cours?.deposit && !coursePayload.deposit) {
                proposals.deposit = null;
                proposals.depositDate = null;
                proposals.proofDeposit = null;
                proposals.proofDeposits = null;
            }
            const updated = await PunctualCourseRepository.updatePunctualCourseByTeachr(cours.id, punctualCourse);
            if (updated && cours?.acceptedProposal_id) {
                const proposal = await CourseProposalRepository.updateCourseProposalCourseByTeachr(cours?.acceptedProposal_id, proposals);
                if (proposal) {
                    dispatch(getActiveCourse(dataDispatch));
                    dispatch(getPendingCourse(dataDispatch));
                    dispatch({ type: SHOW_SUCCESS, payload: `Votre ${getActivePrestationBrandConfig()} a bien été modifié.` });
                    handleCloseEdit();
                }
            }
        } catch (err: any) {
            // les refus du serveur (acompte verrouillé, attestation manquante…)
            // doivent être expliqués, pas seulement journalisés
            console.error(err);
            const code = typeof err?.description === 'string' ? err.description : '';
            const messages: Record<string, string> = {
                deposit_locked_urssaf: `L'acompte de ${getActivePrestationBrandConfig("", "ce ", "cette ")} a déjà été transmis à l'URSSAF et ne peut plus être modifié.`,
                deposit_attestation_required: "Vous devez certifier sur l'honneur l'attestation d'acompte avant d'enregistrer.",
                proof_deposit_required: "Vous devez joindre une preuve d'acompte (PDF, PNG ou JPEG) avant d'enregistrer.",
                deposit_attestation_failed: "La génération de l'attestation d'acompte a échoué. La modification n'a pas été enregistrée, veuillez réessayer.",
                course_session_past_locked: 'Une séance déjà passée ne peut plus être modifiée ni supprimée.',
                course_transmitted_to_urssaf: `${capitalizeFirstLetter(getActivePrestationBrandConfig("", "ce ", "cette "))} a déjà été transmis${getActivePrestationBrandConfig("", "", "e")} à l'URSSAF et ne peut plus être modifié${getActivePrestationBrandConfig("", "", "e")}.`,
                course_over_locked: `${capitalizeFirstLetter(getActivePrestationBrandConfig("", "ce ", "cette "))} est terminé ${getActivePrestationBrandConfig("", "", "e")} et ne peut plus être modifié${getActivePrestationBrandConfig("", "", "e")}.`,
            };
            dispatch({
                type: SHOW_ERROR,
                payload: messages[code]
                    ?? describeOverlapApiError(code)
                    ?? `La modification ${getActivePrestationBrandConfig("", "du ", "de la ")} a échoué. Vérifiez votre saisie puis réessayez.`,
                title: 'Attention !',
            });
        }
        finally { setLoad(false); handleCloseIsOver(); }
    };
    const handleNextButton = async () => {
        if (!validatePunctual()) return;
        // Chevauchement avec un autre cours du prof : toutes les séances sont
        // contrôlées ; le cours modifié est exclu, ses séances stockées vont
        // être remplacées.
        const punctualStart = isCoursePass ? moment(startDateTime).add(1, 'M') : startDateTime;
        const rowsToCheck = cours?.isPunctualMonth
            ? sessionRows
            : [punctualSessionRow(punctualStart, punctualStart, form.duration)];
        const overlap = await fetchExternalOverlap(teachr?.id, rowsToCheck, cours?.id);
        if (overlap) {
            const message = describeExternalOverlap(overlap, rowsToCheck.length);
            if (isCoursePass) {
                handleCloseDeclare();
                handleOpenDeclare(message);
                return;
            }
            handleCloseEdit();
            setBlockingSessions({ open: true, title: 'Créneau déjà occupé', actionLabel: "Modifier l'horaire", message });
            return;
        }
        if (option.isOver) return handleOpenIsOver();
        if (isCoursePass) return handleSubmitEditDeclare();
        return handleSubmitEdit();
    };
    // ── Display helpers ────────────────────────────────────────────────────────
    const displayDate = () =>
        cours?.isPunctualMonth
            ? `du ${moment(startDate).format('DD MMM YYYY')} au ${moment(endDate).format('DD MMM YYYY')}`
            : `pour le ${moment(startDate).format('DD MMM YYYY')}`;

    const displayDateDiologDetail = () => {
        const date = form.date && dayjs.isDayjs(form.date)
            ? moment((form.date as any).format('YYYY-MM-DD'), 'YYYY-MM-DD')
            : moment(form.date);
        return cours?.isPunctualMonth
            ? `du ${moment(form.startTime).format('DD MMM YYYY')} au ${moment(form.endTime).format('DD MMM YYYY')}`
            : `pour le ${date.format('DD MMM YYYY')}`;
    };

    // Montants enregistrés à la déclaration : stables même si le taux de TVA du coach change ensuite
    const prixTotal = vtCoursePrixTotal(cours);
    const amountTtc = isVt
        ? vtCourseRemuneration(cours).toFixed(2)
        : (cours?.priceCf?.priceNoFees ?? cours?.amount?.priceNoFees ?? 0).toFixed(2);

    const displayProofDeposit = () => {
        if (form.deposit && needsDepositAttestation) {
            return (
                <div>
                    {cours?.isPunctualMonth
                        ? displayUploadProofs(
                            form.proofDeposits || [], load,
                            error.proofDeposit, handleError, setFile, handleDeletImageMulti, classes, MAX_PROOFS
                        )
                        : displayUploadProof(
                            Object.values(form?.proofDeposit).length, load,
                            form?.proofDeposit?.contentUrl ?? '', form?.proofDeposit?.filePath ?? '',
                            error.proofDeposit, handleError, setFile, handleDeletImage, classes
                        )}
                    {displayError(error.proofDeposit, errorMsg.proofDeposit, classes)}
                </div>
            );
        }
    };
    const handleError = (v: any) => setErrorMsg((p) => ({ ...p, proofDeposit: v }));

    const displayTerms = () => {
        if (!form.deposit) return null;
        if (!needsDepositAttestation) {
            return (
                <p className={`w-full mt-2 text-sm ${classes.text} ${classes.fontFamily}`}>
                    Attestation sur l'honneur d'acompte signée le {moment(cours?.depositAttestation?.signedAt).format('DD/MM/YYYY')}. Toute modification du montant ou de la date en produira une nouvelle.
                </p>
            );
        }
        return (
            <div className={`w-full space-y-2 font-bold mt-2`}>
                <FormControlLabel
                    className="items-start gap-2"
                    label={
                        <p className={`${error.terms ? classes.error : classes.text} ${classes.fontFamily}`}>
                            <span className="font-bold">Attestation sur l'honneur d'acompte.</span> Je soussigné(e) <strong className={error.terms ? classes.error : classes.primaryText}>{teachr?.firstName} {teachr?.lastName}</strong>, atteste sur l'honneur avoir reçu de {getActiveClientBrandConfig("", "mon ", "mon ")} <strong className={error.terms ? classes.error : classes.primaryText}>{fullName}</strong> la somme de <strong className={error.terms ? classes.error : classes.primaryText}>{form.deposit ? `${parseFloat(String(form.deposit)).toFixed(2)} €` : '…'}</strong> le <strong className={error.terms ? classes.error : classes.primaryText}>{dateOfDeposit ? moment(dateOfDeposit).format('DD/MM/YYYY') : '…'}</strong>, à titre d'acompte pour {getActivePrestationBrandConfig("", "ce ", "cette ")}. Je certifie l'exactitude de cette attestation et je la signe électroniquement en enregistrant. Toute fausse déclaration m'expose à des sanctions. La preuve de versement est obligatoire.
                        </p>
                    }
                    control={<Checkbox checked={!!form.terms} onChange={(e) => setForm((p) => ({ ...p, terms: e.target.checked }))} className={`p-0 ${error.terms ? classes.error : classes.primaryText}`} />}
                    sx={{ color: brandConfig.colors.text }}
                />
                {displayError(error.terms, "Vous devez certifier cette attestation pour continuer", classes)}
            </div>
        );
    };

    // ── Visibilité des boutons ─────────────────────────────────────────────────
    const canDeclare = isCoursePass;
    // Modifier et supprimer (séances passées comprises) tant que le cours n'est
    // ni transmis à l'URSSAF ni terminé. La transmission prime : un cours
    // transmis reste verrouillé même si isOver est encore à false. Les
    // brouillons restent libres. Le serveur applique la même règle.
    const editLockReason: string | null = isCoursePass
        ? null
        : isTransmittedToUrssaf
            ? "Transmis à l'URSSAF"
            : cours?.isOver
                ? `${capitalizeFirstLetter(getActivePrestationBrandConfig())} terminé ${getActivePrestationBrandConfig("", "", "e")}`
                : null;
    const canEdit = editLockReason === null;
    const canDelete = editLockReason === null;
    const handleDeleteClick = () => {
        handleCloseMenu();
        setOption((p) => ({ ...p, deletes: true }));
    };

    // ── Menu ancre ────────────────────────────────────────────────────────────
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleCloseMenu = () => setAnchorEl(null);

    // ── Modifier : page de modification (cours à venir et du mois), modale conservée pour les brouillons ──
    const openEdit = () => {
        handleCloseMenu();
        if (!isCoursePass && cours?.id) {
            navigate(courseEditPath(cours.id), {
                state: { course: cours, clientId: client_id, forChildId: forChild_id, startAt: startAtProp ?? null, child: child ?? null, fromList: true },
            });
            return;
        }
        setSelectedCourse(cours);
        setOption((p) => ({ ...p, edit: true, declare: false }));
    };

    // ── Fiche du cours : mêmes données pour l'en-tête de la fenêtre et son contenu ──
    const detailProps: CoursProps = {
        eleve: fullName,
        matiere: cours?.subjectAsked,
        isCoursePass,
        date: displayDate(),
        heure: moment(startDate).format('HH:mm'),
        duree: Functions.renderDuration(cours.duration),
        remuneration: amountTtc,
        // relation Teach'r : pas de prix total ni de part client, ils comprennent la part de la plateforme
        prixTotal: isVt && !isRelationTeachrCourse(cours) ? prixTotal.toFixed(2) : undefined,
        payeParClient: isVt && !isRelationTeachrCourse(cours) ? vtClientShare(prixTotal).toFixed(2) : undefined,
        tvaAmount: cours?.teachrVat?.vatAmount,
        tvaRate: cours?.teachrVat?.vatRate,
        acompte: cours?.deposit,
        format: cours?.isPunctualMonth
            ? `Plusieurs ${getActivePrestationBrandConfig('s')}`
            : capitalizeFirstLetter(getActivePrestationBrandConfig(' ponctuelle', '', '', ' ponctuel')),
        infos: cours?.globalExtraInformation,
        sessionDescription: cours?.sessionDescriptionOverride ?? null,
        teachrId: teachr?.id ?? null,
        reste: depositStay(),
        proofDeposit: cours?.proofDeposit?.contentUrl ?? '',
        proofDeposits: cours?.isPunctualMonth && Array.isArray(cours?.proofDeposits)
            ? cours.proofDeposits.map((p: any) => p?.contentUrl).filter(Boolean)
            : undefined,
        depositDate: moment(form?.depositDate).format('dddd DD MMMM YYYY'),
        depositAttestation: cours?.depositAttestation ?? null,
        punctual: cours,
        displayStatut: displayStatutCourseDetail(cours?.urssafServiceStatus, cours?.statusDescription, errorsStatusDescription, cours?.isOver, classes, cours?.declarationStatus, cours?.declarationStatusReason, 'left'),
    };
    // ── Rendu ──────────────────────────────────────────────────────────────────
    return (
        <>
            {travelModals}
            {/* P1 – popup bloquant : séances détaillées invalides */}
            <BlockingPopup
                open={blockingSessions.open}
                title={blockingSessions.title ?? "Séances détaillées requises"}
                message={blockingSessions.message}
                actionLabel={blockingSessions.actionLabel ?? "Compléter mes séances"}
                onAction={() => { setBlockingSessions({ open: false, message: '' }); setOption((p) => ({ ...p, edit: true })) }}
            />
            {/* ── Bouton déclencheur ── */}
            <Tooltip title="Actions">
                <IconButton size="small" onClick={handleOpenMenu}>
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            </Tooltip>

            {/* ── Menu dropdown ── */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                slotProps={{ paper: { elevation: 3, sx: { minWidth: 180, borderRadius: 2, "& .MuiTypography-root": { fontFamily: brandConfig?.fontFamily } } } }}
            >
                <MenuItem onClick={() => { handleCloseMenu(); setOption((p) => ({ ...p, showDetail: true })); }}>
                    <ListItemIcon><RemoveRedEyeOutlinedIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText>Consulter</ListItemText>
                </MenuItem>

                {canDeclare && <Divider />}
                {canDeclare && (
                    <MenuItem onClick={() => { handleCloseMenu(); setOption((p) => ({ ...p, declare: true })); }}>
                        <ListItemIcon><SendSharpIcon fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText>Déclarer</ListItemText>
                    </MenuItem>
                )}

                {canEdit ? (
                    <MenuItem onClick={openEdit}>
                        <ListItemIcon><ModeSharpIcon fontSize="small" color="warning" /></ListItemIcon>
                        <ListItemText>Modifier</ListItemText>
                    </MenuItem>
                ) : (
                    // verrouillé : l'entrée reste visible, grisée, avec la raison
                    <MenuItem disabled>
                        <ListItemIcon><ModeSharpIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Modifier" secondary={editLockReason} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                    </MenuItem>
                )}

                <Divider />
                {canDelete ? (
                    <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                        <ListItemIcon><DeleteOutlinedIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Supprimer</ListItemText>
                    </MenuItem>
                ) : (
                    <MenuItem disabled>
                        <ListItemIcon><DeleteOutlinedIcon fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Supprimer" secondary={editLockReason} secondaryTypographyProps={{ fontSize: '0.7rem' }} />
                    </MenuItem>
                )}
            </Menu>

            {/* ── Dialog : Cours déjà passé ── */}
            <DialogLayout
                title={`Déclaration ${getActivePrestationBrandConfig('', "d'un ", "d'une ")} déjà passé`}
                text={
                    <p className={`${classes.text} ${classes.fontFamily}`}>
                        Vous êtes sur le point de déclarer {getActivePrestationBrandConfig('', 'un ', 'une ')} déjà réalisé {displayDateDiologDetail()}.
                        Veuillez noter qu'une fois déclaré,{' '}
                        <strong className="text-third underline">{getActivePrestationBrandConfig('', 'ce ', 'cette ')} ne pourra plus être modifié ni supprimé</strong>.
                    </p>
                }
                open={option.openIsOver}
                cancel="Retour"
                valid="Valider"
                load={load}
                handleSubmit={isCoursePass ? handleSubmitEditDeclare : handleSubmitEdit}
                handleClose={handleCloseIsOver}
            />

            {/* ── Dialog : Modification ── */}
            <DialogLayout
                title={`Modification ${isCoursePass ? 'du brouillon' : getActivePrestationBrandConfig('', 'du ', 'de la ')}`}
                text={
                    <AlertInfo
                        info={isCoursePass
                            ? (coursesThisMonth >= 8 || wouldExceedLimit ? 'error'
                                : coursesThisMonth >= 7 || alreadyDeclaredAmount >= maxNet * 0.82 ? 'warning'
                                    : selectedCourse?.message ? 'warning' : 'info')
                            : (selectedCourse?.message ? 'warning' : 'info')
                        }
                        msg={
                            <div>
                                {selectedCourse?.message
                                    ? <p>{selectedCourse.message} Vous pouvez également apporter des modifications à {getActivePrestationBrandConfig('', 'ce ', 'cette ')} à votre {getActiveClientBrandConfig()} <strong>{capitalizeFirstLetter(fullName)}</strong>.</p>
                                    : <p>Vous êtes sur le point de modifier {getActivePrestationBrandConfig('', 'le ', 'la ')} de votre {getActiveClientBrandConfig()} <strong>{capitalizeFirstLetter(fullName)}</strong>, {displayDate()}.</p>
                                }
                                {isCoursePass && (
                                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.12)', fontSize: '0.85em' }}>
                                        <p style={{ fontWeight: 600, marginBottom: 4 }}>{currentMonthDisplayName} - {fullName} :</p>
                                        <ul style={{ margin: '0 0 6px', paddingLeft: 20 }}>
                                            <li>
                                                {capitalizeFirstLetter(getActivePrestationBrandConfig('s'))} ce mois :{' '}
                                                <strong style={{ color: quotaText.tone === 'danger' ? '#d32f2f' : quotaText.tone === 'warning' ? '#ed6c02' : 'inherit' }}>
                                                    {quotaText.headline}
                                                </strong>
                                                {' '}({quotaText.detail})
                                            </li>
                                            {/* <li>Heures déclarées : <strong>{childHoursStr}</strong></li> */}
                                            <li>
                                                Montant déclaré :{' '}
                                                <strong style={{ color: alreadyDeclaredAmount >= maxNet ? '#d32f2f' : alreadyDeclaredAmount >= maxNet * 0.82 ? '#ed6c02' : 'inherit' }}>
                                                    {alreadyDeclaredAmount.toFixed(2)}€
                                                </strong>
                                                {' '}/ {maxNet.toFixed(0)}€
                                                {wouldExceedLimit && <> → <strong style={{ color: '#d32f2f' }}>total {totalDeclIfAdded.toFixed(0)}€ (plafond dépassé !)</strong></>}
                                            </li>
                                        </ul>
                                        <p style={{ fontStyle: 'italic', fontSize: '0.9em', marginTop: 4 }}>
                                            {coursesThisMonth >= 8 || wouldExceedLimit
                                                ? `Quota atteint ce mois. Vous pouvez déclarer sur un autre mois calendaire.`
                                                : `Maximum : 8 cours et ${maxNet.toFixed(0)}€ ${isVt ? 'TTC par client' : 'net par élève'} par mois calendaire.`
                                            }
                                        </p>
                                    </div>
                                )}
                            </div>
                        }
                    />
                }
                valid={isCoursePass ? 'Déclarer' : 'Modifier'}
                cancel="Annuler"
                open={option.edit}
                load={load}
                maxWidth="md"
                handleSubmit={handleNextButton}
                handleClose={handleCloseEdit}
                handleCloseIcon={handleCloseEdit}
                close={load}
            >
                <>
                    {/* Bannière quota mensuel */}
                    {child && (
                        <Alert
                            severity={coursesThisMonth >= 8 || wouldExceedLimit ? 'warning' : 'info'}
                            variant="outlined"
                            sx={{ mb: 2, fontSize: '0.82rem' }}
                        >
                            {cours?.isPunctualMonth
                                ? `Plusieurs ${getActivePrestationBrandConfig('s')}`
                                : capitalizeFirstLetter(getActivePrestationBrandConfig(' ponctuelle', "d'un ", "d'une ", ' ponctuel'))
                            }{' '}pour <strong>{fullName || `votre ${getActiveClientBrandConfig()}`}</strong> - <strong>{currentMonthDisplayName}</strong> :
                            <ul style={{ margin: '4px 0 2px', paddingLeft: 20 }}>
                                <li>
                                    {capitalizeFirstLetter(getActivePrestationBrandConfig('s'))} ce mois : <strong style={{ color: quotaText.tone === 'danger' ? '#d32f2f' : quotaText.tone === 'warning' ? '#ed6c02' : 'inherit' }}>{quotaText.headline}</strong>
                                    {' '}({quotaText.detail})
                                </li>
                                {/* <li>Heures déclarées : <strong>{childHoursStr}</strong></li> */}
                                <li>
                                    Montant déclaré : <strong>{alreadyDeclaredAmount.toFixed(2)}€</strong>
                                    {wouldExceedLimit
                                        ? <> → <strong style={{ color: '#d32f2f' }}>total {totalDeclIfAdded.toFixed(0)}€ (plafond {maxNet.toFixed(0)}€ dépassé !)</strong></>
                                        : <> / {maxNet.toFixed(0)}€</>
                                    }
                                </li>
                            </ul>
                            {coursesThisMonth >= 8
                                ? <>Quota de cours atteint ce mois. Sélectionnez une date dans un autre mois.</>
                                : <>Maximum : 8 cours et {maxNet.toFixed(0)}€ {isVt ? 'TTC par client' : 'par élève'} par mois.</>
                            }
                        </Alert>
                    )}
                    <FormEditCourse
                        conditionScoop={conditionScoop}
                        errorDate={error.date} errorMsgDate={errorMsg.date}
                        errorDeposit={error.deposit} errorMsgDeposit={errorMsg.deposit}
                        errorPrice={error.price} msgErrorPrice={errorMsg.price}
                        errorDates={error.dates} msgErrorDates={errorMsg.dates}
                        errorEndTime={error.endTime} msgErrorEndTime={errorMsg.endTime}
                        setStartTime={(v: any) => setForm((p) => ({ ...p, startTime: v }))}
                        setEndTime={(v: any) => setForm((p) => ({ ...p, endTime: v }))}
                        setDatesValues={(v: any) => setForm((p) => ({ ...p, datesValues: v }))}
                        startTime={form.startTime}
                        dateTime={form.date}
                        valueDate={form.date}
                        duration={form.duration}
                        setDate={(v: string) => {
                            if (!v) return;
                            const dc2 = v && dayjs.isDayjs(v) ? moment((v as any).format('YYYY-MM-DD'), 'YYYY-MM-DD') : moment(v);
                            if (moment(dc2).format('YYYY-MM-DD') < moment(startAt).format('YYYY-MM-DD')) {
                                setErrorMonth({ error: true, msg: `La date ne peut pas être antérieure à la période de déclaration.` });
                            } else if (!cours?.isPunctualMonth && dc2.format('YYYY-MM') === moment().format('YYYY-MM') && coursesThisMonth >= 8) {
                                setErrorMonth({ error: true, msg: `Quota de 8 cours atteint pour ${currentMonthDisplayName}. Veuillez sélectionner une date dans un autre mois calendaire.` });
                            } else {
                                setErrorMonth({ error: moment(dc2).format('YYYY-MM') < moment().format('YYYY-MM'), msg: `Vous ne pouvez pas déclarer pour un mois déjà passé.` });
                            }
                            if (dc2.format('YYYY-MM-DD') === moment(startAt).format('YYYY-MM-DD')) {
                                const startAtMin = moment(startAt).add(2.5, 'hours');
                                const endAtMin = moment(startAtMin).add(form.duration, 'hours');
                                setForm((p) => ({ ...p, startTime: moment(startAtMin).minute(getMinute(startAtMin.minute())).toDate(), endTime: moment(endAtMin).minute(getMinute(endAtMin.minute())).toDate() }));
                            }
                            setOption((p) => ({ ...p, isOver: dc2.format('YYYY-MM-DD') < now }));
                            setForm((p) => ({ ...p, date: v }));
                        }}
                        defaultResult={defaultResult}
                        customDuration={isTeachrContact ? (cours?.isPunctualMonth ? (form.deposit ? '' : 'col-span-2') : '') : (form.deposit ? '' : 'col-span-2')}
                        customPrice={isTeachrContact ? (cours?.isPunctualMonth ? (form.deposit ? '' : 'col-span-2') : '') : 'hidden'}
                        customDepositStay={cours?.isPunctualMonth ? (form.deposit ? '' : 'col-span-2') : ''}
                        customMultiDatePicker={form.deposit ? 'col-span-1' : 'col-span-2'}
                        valueDuration={Functions.renderDuration(form.duration * 2)}
                        isPunctualMonth={cours?.isPunctualMonth}
                        setDuration={(cb: any) => setForm((p) => ({ ...p, duration: cb(p.duration) }))}
                        setTeachrPrice={(v: string) => {
                            setForm((p) => ({ ...p, teachrPrice: v }));
                            const pv = validatePrice(v, form.duration);
                            setError((p) => ({ ...p, price: !pv.isValid }));
                            setErrorMsg((p) => ({ ...p, price: pv.message }));
                        }}
                        price={form.teachrPrice}
                        customBody="grid md:grid-cols-2 grid-cols-1 gap-4 md:gap-4"
                        customAll="py-4 space-y-2"
                        startAt={startAt}
                        setDesposit={(v: any) => setForm((p) => ({ ...p, deposit: parseFloat(v) }))}
                        deposit={form.deposit}
                        depositStay={depositStay()}
                        amount={amount}
                        depositDate={form.depositDate}
                        setDepositDate={(v: any) => setForm((p) => ({ ...p, depositDate: v }))}
                        errorDepositDate={error.dateDeposit}
                        errorDepositMsg={errorMsg.dateDeposit}
                        proof={displayProofDeposit()}
                        displayTerms={displayTerms()}
                        isTeachrContact={isTeachrContact}
                        setIsOver={(v: boolean) => setOption((p) => ({ ...p, isOver: v }))}
                        errorMsgMonth={displayError(errorMonth.error, errorMonth.msg, classes)}
                        proofDeposit={cours?.proofDeposit?.contentUrl ?? ''}
                        proofDeposits={cours?.isPunctualMonth && Array.isArray(cours?.proofDeposits)
                            ? cours.proofDeposits.map((p: any) => p?.contentUrl).filter(Boolean)
                            : undefined}
                        isCoursePass={isCoursePass}
                        isProof={form.deposit && form.deposit !== cours?.deposit ? false : true}
                        sessionList={cours?.isPunctualMonth ? (
                            <div className="md:col-span-2 w-full">
                                <SessionList
                                    rows={sessionRows}
                                    onChange={handleSessionsChange}
                                    startAt={startAt}
                                    setIsOver={(v: boolean) => setOption((p) => ({ ...p, isOver: v }))}
                                    minSessions={minSessions}
                                />
                            </div>
                        ) : undefined}
                    />
                    {/* Déroulement des séances : description habituelle, personnalisable pour ce cours.
                        Montée seulement dialogue ouvert, pour ne pas charger la description sur chaque ligne. */}
                    {(option.edit || option.editAndDeclare) && (
                        <>
                            <SessionDescriptionCard
                                teachr={teachr}
                                value={form.sessionDescriptionOverride ?? null}
                                onChange={(next) => { setForm((p) => ({ ...p, sessionDescriptionOverride: next })); setDescriptionError(''); }}
                                onGenericLoaded={setGenericDescription}
                            />
                            {displayError(descriptionError !== '', descriptionError, classes)}
                        </>
                    )}
                </>
            </DialogLayout>

            {/* ── Dialog : Détail ── */}
            <DialogLayout
                // l'en-tête de la fiche (matière, élève, statut, résumé) remplace le titre « Détails du cours »
                title={option.showDetail ? <DetailCoursV2Header {...detailProps} /> : ''}
                open={option.showDetail}
                load={load}
                maxWidth="md"
                handleClose={() => setOption((p) => ({ ...p, showDetail: false }))}
                customDialogActions="hidden"
                handleCloseIcon={() => setOption((p) => ({ ...p, showDetail: false }))}
            >
                {/* Autre justificatif de déplacement (cours en attente d'approbation ou rejeté) */}
                <CourseTravelProofUpload courseId={cours?.id} declarationStatus={cours?.declarationStatus} />
                <DetailCours {...detailProps} />
            </DialogLayout>

            {/* ── Dialog : Suppression ── */}
            <DialogLayout
                title={`Suppression ${isCoursePass ? 'du brouillon' : getActivePrestationBrandConfig('', 'du ', 'de la ')}`}
                text={
                    <p className={`${classes.text} ${classes.fontFamily}`}>
                        Vous êtes sur le point de supprimer le {isCoursePass ? `brouillon ${getActivePrestationBrandConfig('', 'du ', 'de la ')}` : getActivePrestationBrandConfig('', 'du ', 'de la ')} de{' '}
                        <strong>{fullName}</strong>, du <strong>{displayDate()}</strong>.
                        Si vous souhaitez confirmer, cliquez sur <strong>Supprimer</strong>.
                    </p>
                }
                valid="Supprimer"
                cancel="Annuler"
                open={option.deletes}
                load={load}
                handleSubmit={() => handleSubmitDelete(cours?.id, cours?.clientId, setLoad, setOption)}
                handleClose={() => setOption((p) => ({ ...p, deletes: false }))}
            />

            {/* ── Dialog : Déclaration ── */}
            <DialogLayout
                title={`Déclaration ${getActivePrestationBrandConfig('', "d'un ", "d'une ")} ✏️`}
                text={
                    <p className={`${classes.text} ${classes.fontFamily}`}>
                        Vous êtes sur le point de déclarer le {getActivePrestationBrandConfig('', 'le ', 'la ')} de{' '}
                        <strong>{fullName}</strong>, programmé <strong>{displayDate()}</strong>.
                        Si vous souhaitez confirmer, cliquez sur <strong>Déclarer</strong>.
                    </p>
                }
                valid="Déclarer"
                cancel="Annuler"
                open={option.declare}
                load={load}
                handleSubmit={handleSubmitDeclare}
                handleClose={handleCloseDeclare}
            />
        </>
    );
};

export default CourseRowActions;
